/**
 * Where everything lives, in one place.
 *
 * The old shell put twelve destinations in a row that scrolled sideways and
 * nine more behind a "menu". Measured on a 390px phone that row was 886px
 * wide: five items visible, seven invisible, no affordance saying the rest were
 * a swipe away, and the active one never scrolled into view. A coach who cannot
 * see a screen does not have it.
 *
 * Navigation is now two renderings of this one model — a bottom bar on a phone
 * and a grouped sidebar on a desktop — and both read from here, because a
 * second list is a list that drifts.
 *
 * It sits in `lib` rather than beside the components for the same reason
 * `weeksFrom` does: the question "can every screen be reached, and does every
 * label exist" is answerable without a renderer, so it is answered in a test.
 *
 * The grouping is the loop the product runs on rather than an alphabet of
 * nouns: what you are doing now, the match it is for, the players doing it,
 * what it showed, and the things set up once a season.
 */

/** Every destination, so an icon map and a label check can both be exhaustive. */
export const NAV_KEYS = [
  'dashboard',
  'plan',
  'sessions',
  'exercises',
  'calendar',
  'matches',
  'formations',
  'squad',
  'register',
  'development',
  'load',
  'objectives',
  'decisions',
  'blocks',
  'reports',
  'teams',
  'seasons',
  'import',
  'settings',
  'guide',
  'ask',
] as const;

export type NavKey = (typeof NAV_KEYS)[number];

export type NavItem = {
  readonly to: string;
  readonly key: NavKey;
  /** `end` so Today does not light up on every route beneath `/`. */
  readonly end?: boolean;
};

export type NavGroup = {
  readonly key: string;
  readonly items: readonly NavItem[];
};

const ITEMS = {
  dashboard: { to: '/', key: 'dashboard', end: true },
  plan: { to: '/plan', key: 'plan' },
  sessions: { to: '/sessions', key: 'sessions' },
  exercises: { to: '/exercises', key: 'exercises' },
  calendar: { to: '/calendar', key: 'calendar' },
  matches: { to: '/matches', key: 'matches' },
  formations: { to: '/formations', key: 'formations' },
  squad: { to: '/squad', key: 'squad' },
  register: { to: '/register', key: 'register' },
  development: { to: '/development', key: 'development' },
  load: { to: '/load', key: 'load' },
  objectives: { to: '/objectives', key: 'objectives' },
  decisions: { to: '/decisions', key: 'decisions' },
  blocks: { to: '/blocks', key: 'blocks' },
  reports: { to: '/reports', key: 'reports' },
  teams: { to: '/teams', key: 'teams' },
  seasons: { to: '/seasons', key: 'seasons' },
  import: { to: '/import', key: 'import' },
  settings: { to: '/settings', key: 'settings' },
  guide: { to: '/guide', key: 'guide' },
  ask: { to: '/ask', key: 'ask' },
} as const satisfies Record<NavKey, NavItem>;

/**
 * The four destinations in the phone's bottom bar.
 *
 * The weekly rhythm in the order it runs. A fifth slot opens the More sheet,
 * which is a list to read rather than a row you have to guess scrolls.
 */
export const TABS: readonly NavItem[] = [
  ITEMS.dashboard,
  ITEMS.plan,
  ITEMS.sessions,
  ITEMS.matches,
];

/**
 * Pinned to the top of the More sheet.
 *
 * The four most-opened screens that did not win a tab. With these, nothing in
 * the product is more than two taps from anywhere.
 */
export const PINNED: readonly NavItem[] = [
  ITEMS.squad,
  ITEMS.load,
  ITEMS.register,
  ITEMS.ask,
];

/**
 * Sits above the groups in both renderings.
 *
 * The assistant is asked from wherever the coach happens to be standing, so it
 * does not belong inside one phase of the week.
 */
export const ASSISTANT: NavItem = ITEMS.ask;

/** Every destination, grouped. Twenty, plus the assistant above them. */
export const GROUPS: readonly NavGroup[] = [
  {
    key: 'now',
    items: [ITEMS.dashboard, ITEMS.plan, ITEMS.sessions, ITEMS.exercises, ITEMS.calendar],
  },
  { key: 'match', items: [ITEMS.matches, ITEMS.formations] },
  { key: 'players', items: [ITEMS.squad, ITEMS.register, ITEMS.development] },
  {
    key: 'review',
    items: [ITEMS.load, ITEMS.objectives, ITEMS.decisions, ITEMS.blocks, ITEMS.reports],
  },
  {
    key: 'setup',
    items: [ITEMS.teams, ITEMS.seasons, ITEMS.import, ITEMS.settings, ITEMS.guide],
  },
];

/** Every destination the navigation offers, for a test to count. */
export const ALL_ITEMS: readonly NavItem[] = [ASSISTANT, ...GROUPS.flatMap((g) => g.items)];
